import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { Img } from "@/components/common";
import { cn } from "@/lib/utils";

const clamp = (n) => Math.min(100, Math.max(0, n));

export const BeforeAfter = ({ before, after, alt, ratio = "3/2", testId = "before-after-slider" }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);
  const portrait = ratio.split("/").map(Number).reduce((a, b) => a / b) < 1;

  const update = useCallback((x) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || !r.width || !Number.isFinite(x)) return;
    setPos(clamp(((x - r.left) / r.width) * 100));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      update(e.clientX);
      e.cancelable && e.preventDefault();
    };
    const end = () => (dragging.current = false);
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [update]);

  const onPointerDown = (e) => {
    if (e.button > 0) return;
    dragging.current = true;
    update(e.clientX);
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {}
  };

  const onKey = (e) => {
    const step = e.shiftKey ? 10 : 2;
    const map = { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step, PageDown: -10, PageUp: 10 };
    if (e.key in map) setPos((p) => clamp(p + map[e.key]));
    else if (e.key === "Home") setPos(0);
    else if (e.key === "End") setPos(100);
    else return;
    e.preventDefault();
  };

  return (
    <div
      ref={ref}
      data-testid={testId}
      className={cn("relative mx-auto w-full cursor-ew-resize select-none overflow-hidden rounded-lg bg-surface frame-shadow", portrait && "max-w-[520px]")}
      style={{ aspectRatio: ratio, touchAction: "pan-y" }}
      onPointerDown={onPointerDown}
      onClick={(e) => update(e.clientX)}
      onDragStart={(e) => e.preventDefault()}
    >
      <Img src={after} alt={`${alt} — after`} className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      <div className="pointer-events-none absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} data-testid={`${testId}-before-layer`}>
        <Img src={before} alt={`${alt} — before`} className="absolute inset-0 h-full w-full object-cover" />
      </div>
      <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur">Before</span>
      <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-turq px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-onturq">After</span>
      <div className="pointer-events-none absolute inset-y-0 w-[2px] -translate-x-1/2 bg-white shadow-[0_0_20px_rgba(46,211,198,0.8)]" style={{ left: `${pos}%` }} />
      <div
        role="slider"
        tabIndex={0}
        aria-label="Before and after comparison. Use arrow keys to move."
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        aria-valuetext={`${Math.round(pos)}% before visible`}
        onKeyDown={onKey}
        data-testid={`${testId}-handle`}
        className="absolute top-1/2 grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-white bg-turq text-onturq shadow-xl transition-transform duration-200 hover:scale-110 focus-visible:scale-110"
        style={{ left: `${pos}%` }}
      >
        <ChevronsLeftRight className="h-5 w-5" aria-hidden />
      </div>
    </div>
  );
};
